"use client";

import type { ComponentType, ReactElement } from "react";
import { useEffect, useMemo, useRef } from "react";

import { isPreviewing } from "@builder.io/sdk-react";

import { validateTriggerCompatibility } from "./validators/triggerCompatibilityRules";

import type { OnsitePersonalizationModalProps } from "./OnsitePersonalizationModal";

type TriggerCompatibilityViolations = ReturnType<typeof validateTriggerCompatibility>;

const COMPONENT_NAME = "OnsitePersonalizationModal";

const logPreviewViolations = (violations: TriggerCompatibilityViolations) => {
  console.info(`[WARN] ${COMPONENT_NAME} — trigger compatibility issues:`);
  console.info(JSON.stringify({ componentName: COMPONENT_NAME, violations }, null, 2));
};

const logProductionViolations = (violations: TriggerCompatibilityViolations) => {
  console.error(`[ERROR] ${COMPONENT_NAME} — invalid trigger configuration. Component will not render.`);
  console.error(JSON.stringify({ componentName: COMPONENT_NAME, violations }, null, 2));
};

/**
 * Wraps the OnsitePersonalizationModal with cross-field trigger compatibility
 * validation. Runs after the structural Zod validation from `withValidation`.
 *
 * In preview/author mode violations are logged as warnings (the modal still
 * renders so content managers can see their work). In production, a violation
 * blocks render and logs an error, matching the behaviour of `withValidation`.
 */
export function withTriggerCompatibilityValidation(
  Component: ComponentType<OnsitePersonalizationModalProps>
): ComponentType<OnsitePersonalizationModalProps> {
  return function TriggerCompatibilityGuard(props: OnsitePersonalizationModalProps): ReactElement | null {
    const {
      isInactivityModalEnabled,
      isQuickSwipeUpModalEnabled,
      isTimeOnPageEnabled,
      isExitIntentEnabled,
      isScrollDepthModalEnabled,
      isPurchasedDaysAgoModalEnabled,
      isUserActionTriggerEnabled,
      isLocalTimeTriggerEnabled,
      triggerMode,
      hideModalExpireTime,
      frequencyType,
      frequencyValue,
      minCartItemCount,
    } = props;

    const violations = useMemo(
      () =>
        validateTriggerCompatibility({
          isInactivityModalEnabled,
          isQuickSwipeUpModalEnabled,
          isTimeOnPageEnabled,
          isExitIntentEnabled,
          isScrollDepthModalEnabled,
          isPurchasedDaysAgoModalEnabled,
          isUserActionTriggerEnabled,
          isLocalTimeTriggerEnabled,
          triggerMode: triggerMode ?? "or",
          hideModalExpireTime: hideModalExpireTime !== undefined ? String(hideModalExpireTime) : undefined,
          frequencyType,
          frequencyValue,
          minCartItemCount,
        }),
      [
        isInactivityModalEnabled,
        isQuickSwipeUpModalEnabled,
        isTimeOnPageEnabled,
        isExitIntentEnabled,
        isScrollDepthModalEnabled,
        isPurchasedDaysAgoModalEnabled,
        isUserActionTriggerEnabled,
        isLocalTimeTriggerEnabled,
        triggerMode,
        hideModalExpireTime,
        frequencyType,
        frequencyValue,
        minCartItemCount,
      ]
    );

    const isPreview = isPreviewing();
    const lastLoggedDedupeKeyRef = useRef<string | null>(null);

    useEffect(() => {
      if (violations.length === 0) {
        lastLoggedDedupeKeyRef.current = null;
        return;
      }

      const dedupeKey = `${isPreview ? "preview" : "production"}:${JSON.stringify(violations)}`;
      if (lastLoggedDedupeKeyRef.current === dedupeKey) return;
      lastLoggedDedupeKeyRef.current = dedupeKey;

      if (isPreview) {
        logPreviewViolations(violations);
        return;
      }
      logProductionViolations(violations);
    }, [isPreview, violations]);

    if (violations.length > 0 && !isPreview) {
      return null;
    }

    return <Component {...props} />;
  };
}
